import { createRouter, createWebHistory } from 'vue-router'
import LoginView from '@/views/LoginView.vue'
import DashboardView from '@/views/DashboardView.vue'
import SignupView from '@/views/SignupView.vue'
import CompleteProfileView from '@/views/CompleteProfileView.vue'
import EmployeeApplicationPromptView from '@/views/EmployeeApplicationPromptView.vue'
import EmployeeVerificationView from '@/views/EmployeeVerificationView.vue'
import EmployeeFormStepView from '@/views/EmployeeFormStepView.vue'
import EmployeePendingApprovalView from '@/views/EmployeePendingApprovalView.vue'
import { auth } from '@/firebase'
import { onAuthStateChanged } from 'firebase/auth'
import {
  EMPLOYEE_PORTAL_STATUSES,
  getEmployeePortalAccess,
  getUserRoleFromProfile,
  resolveUserProfile,
} from '@/services/userProfile'

const routes = [
  { path: '/', redirect: { name: 'Login' } },
  { path: '/login', name: 'Login', component: LoginView, meta: { guestOnly: true } },
  { path: '/dashboard', name: 'Dashboard', component: DashboardView, meta: { requiresAuth: true } },
  { path: '/signup', name: 'Signup', component: SignupView },
  { path: '/complete-profile', name: 'CompleteProfile', component: CompleteProfileView, meta: { requiresAuth: true } },
  { path: '/employee/apply', name: 'EmployeeApplicationPrompt', component: EmployeeApplicationPromptView, meta: { requiresAuth: true, employeePortal: true } },
  { path: '/employee/verification', name: 'EmployeeVerification', component: EmployeeVerificationView, meta: { requiresAuth: true, employeePortal: true } },
  { path: '/employee/form', name: 'EmployeeFormStep', component: EmployeeFormStepView, meta: { requiresAuth: true, employeePortal: true } },
  { path: '/employee/pending', name: 'EmployeePendingApproval', component: EmployeePendingApprovalView, meta: { requiresAuth: true, employeePortal: true } },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

const getCurrentUser = () =>
  new Promise((resolve) => {
    const unsub = onAuthStateChanged(auth, (user) => {
      unsub()
      resolve(user)
    })
  })

const getDefaultAuthenticatedRoute = async (user) => {
  const profile = await resolveUserProfile(user).catch(() => null)
  return getUserRoleFromProfile(profile) === 'Owner' ? 'Dashboard' : 'CompleteProfile'
}

router.beforeEach(async (to, from, next) => {
  const user = await getCurrentUser()
  const isAuthenticated = !!user
  if (to.meta?.requiresAuth && !isAuthenticated) {
    next({ name: 'Login' })
    return
  }

  if (to.meta?.guestOnly && isAuthenticated) {
    next({ name: await getDefaultAuthenticatedRoute(user) })
    return
  }

  if (to.name === 'CompleteProfile' && isAuthenticated) {
    const routeName = await getDefaultAuthenticatedRoute(user)
    if (routeName !== 'CompleteProfile') {
      next({ name: routeName })
      return
    }
    next()
    return
  }

  if (to.meta?.employeePortal && isAuthenticated) {
    try {
      const access = await getEmployeePortalAccess(user)
      const isDeniedRetryVerification =
        access.status === EMPLOYEE_PORTAL_STATUSES.DENIED
        && to.name === 'EmployeeVerification'
        && to.query?.retry === '1'

      if (!isDeniedRetryVerification && to.name !== access.routeName) {
        next({ name: access.routeName })
        return
      }
    } catch {
      next({ name: 'CompleteProfile' })
      return
    }
  }

  next()
})

export default router
